import { CloudOff, RefreshCw, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useOutboxSummary } from "@/lib/queries";
import { useLocal, useOnline, useSyncState } from "@/local/LocalProvider";

function Bar({ icon, children, action }: { icon: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div
      role="status"
      className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-ink-2"
    >
      <span className="shrink-0 text-ink-3" aria-hidden>{icon}</span>
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </div>
  );
}

const changes = (n: number) => `${n} change${n === 1 ? "" : "s"}`;

/** Tells the user, plainly, where their data stands: offline, failing to sync, or not persisted. */
export function SyncBanner() {
  const online = useOnline();
  const sync = useSyncState();
  const { persistent, engine } = useLocal();
  const { data: outbox } = useOutboxSummary();
  const waiting = outbox?.waiting ?? 0;
  const attention = outbox?.attention ?? 0;

  // Refused changes come first: they need the user, and they must never sit unnoticed.
  const needsAttention = attention > 0 && (
    <Bar
      icon={<TriangleAlert className="size-4" />}
      action={<Link to="/sync-issues" className="font-medium text-accent hover:underline">Review</Link>}
    >
      <strong className="font-medium text-ink">{changes(attention)} couldn't be applied.</strong> The server
      didn't accept {attention === 1 ? "it" : "them"}; your ledger shows the server's version.
    </Bar>
  );

  if (!online || sync.status === "offline") {
    return (
      <>
        {needsAttention}
        <Bar icon={<CloudOff className="size-4" />}>
          <strong className="font-medium text-ink">You're offline.</strong> Showing the data saved on this
          device.{" "}
          {waiting > 0
            ? `${changes(waiting)} will be sent automatically when you reconnect.`
            : "It refreshes automatically when you reconnect."}
        </Bar>
      </>
    );
  }

  if (sync.status === "error") {
    return (
      <>
        {needsAttention}
      <Bar
        icon={<TriangleAlert className="size-4" />}
        action={
          <button
            type="button"
            onClick={() => void engine?.sync()}
            className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
          >
            <RefreshCw className="size-3.5" aria-hidden /> Retry
          </button>
        }
      >
        Couldn't sync just now. What you see is the last data saved on this device.
        {waiting > 0 && ` ${changes(waiting)} will be sent as soon as sync works.`}
      </Bar>
      </>
    );
  }

  if (!persistent) {
    return (
      <>
        {needsAttention}
        <Bar icon={<TriangleAlert className="size-4" />}>
          This browser can't keep your data on the device, so it won't be available offline or after a
          reload. Your data is safe on the server.
          {waiting > 0 && ` Keep this tab open until ${changes(waiting)} ${waiting === 1 ? "has" : "have"} synced.`}
        </Bar>
      </>
    );
  }

  return needsAttention || null;
}

/** "Synced 3 min ago" for the sidebar. */
export function SyncedAgo() {
  const sync = useSyncState();
  const { data: outbox } = useOutboxSummary();
  if (!sync.lastSyncAt) return null;
  const minutes = Math.max(0, Math.round((Date.now() - new Date(sync.lastSyncAt).getTime()) / 60_000));
  const base = sync.status === "syncing" ? "Syncing…" : minutes < 1 ? "Synced just now" : `Synced ${minutes} min ago`;
  const waiting = outbox?.waiting ?? 0;
  return <p className="px-2 text-xs text-ink-3">{waiting > 0 ? `${base} · ${waiting} waiting` : base}</p>;
}
